
// Core analytics functions
import ReactGA from 'react-ga4';

// Extend Window interface to include gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

// Initialize GA4 with your actual tracking ID
if (!window.gtag) {
  ReactGA.initialize('G-FCMHK96H3W');
}

// Core tracking function
export const trackEvent = (eventName: string, parameters?: Record<string, unknown>) => {
  console.log('Tracking event:', eventName, parameters);
  
  try {
    ReactGA.event(eventName, parameters);
  } catch (error) {
    console.error('GA4 tracking error:', error);
  }
};

// Authentication tracking functions
export const trackLoginAttempt = (method: string = 'email') => {
  trackEvent('login_attempt', { method });
};

export const trackLoginSuccess = (method: string = 'email', userId?: string) => {
  const params: Record<string, unknown> = { method };
  if (userId) {
    params.user_id = userId;
  }
  trackEvent('login', params);
};

export const trackLoginFailure = (error: string, method: string = 'email') => {
  trackEvent('login_failure', { error, method });
};

export const trackSignupAttempt = (method: string = 'email') => {
  trackEvent('signup_attempt', { method });
};

export const trackSignupSuccess = (method: string = 'email', userId?: string) => {
  const params: Record<string, unknown> = { method };
  if (userId) {
    params.user_id = userId;
  }
  trackEvent('sign_up', params);
};

export const trackSignupFailure = (error: string, method: string = 'email') => {
  trackEvent('signup_failure', { error, method });
};

export const trackLogout = (userId?: string) => {
  trackEvent('logout', userId ? { user_id: userId } : {});
};

export const trackUserAuthenticated = (userId: string, email?: string) => {
  trackEvent('user_authenticated', { user_id: userId, email_domain: email?.split('@')[1] });
  ReactGA.set({ userId });
};

// Email verification tracking
export const trackEmailVerificationSent = (email: string) => {
  trackEvent('email_verification_sent', {
    email_domain: email.split('@')[1]
  });
};

// Password reset tracking
export const trackPasswordResetRequest = (email: string) => {
  trackEvent('password_reset_request', {
    email_domain: email.split('@')[1]
  });
};

export const trackPasswordResetSuccess = (email?: string) => {
  const params: Record<string, unknown> = {};
  if (email) {
    params.email_domain = email.split('@')[1];
  }
  trackEvent('password_reset_success', params);
};

export const trackPasswordResetFailure = (error: string, email?: string) => {
  const params: Record<string, unknown> = { error };
  if (email) {
    params.email_domain = email.split('@')[1];
  }
  trackEvent('password_reset_failure', params);
};

export const trackPasswordUpdateSuccess = () => {
  trackEvent('password_update_success');
};

export const trackPasswordUpdateFailure = (error: string) => {
  trackEvent('password_update_failure', { error });
};

// Auth UI tracking
export const trackAuthUIModeChange = (mode: 'login' | 'signup') => {
  trackEvent('auth_ui_mode_change', { mode });
};

export const trackResendVerificationClick = (email: string) => {
  trackEvent('resend_verification_click', {
    email_domain: email.split('@')[1]
  });
};

export const trackBackToSignInClick = () => {
  trackEvent('back_to_signin_click');
};

// Candidate tracking functions (existing)
export const trackCandidateCreated = (candidate: { id: string; first_name?: string; last_name?: string; status?: string }) => {
  trackEvent('candidate_created', {
    candidate_id: candidate.id,
    status: candidate.status,
    engagement_time_msec: '1',
  });
};

export const trackCandidateStatusChanged = (candidateId: string, newStatus: string, oldStatus?: string, candidateName?: string) => {
  trackEvent('candidate_status_change', {
    candidate_id: candidateId,
    from_status: oldStatus,
    to_status: newStatus,
  });
};

export const trackCandidateUpdated = (candidateId: string, updatedFields: string[], candidateName?: string) => {
  trackEvent('candidate_updated', {
    candidate_id: candidateId,
    updated_fields: updatedFields.join(','),
  });
};

export const trackCandidateViewed = (candidateId: string, candidateName?: string) => {
  trackEvent('candidate_profile_view', {
    candidate_id: candidateId,
  });
};

// Export all tracking modules
export * from './candidateTracking';
export * from './authTracking';
export * from './emailAnalytics';
export * from './automationAnalytics';
export * from './jobTracking';
export * from './clientTracking';
export * from './revenueTracking';
export * from './leadTracking';
export * from './navigationTracking';
